import {Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {DialogService, DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {ViewportService} from 'src/app/services/viewport.service';
import {Airport, DEAL_FREQUENCY_CLASS_MAP, DealFrequency, UserDeparture} from '../../../models/AirportModel';
import {ToastrService} from 'ngx-toastr';
import {AirportsService} from '../../../services/airports.service';
import {UserModel} from '../../../models/User';
import {DealsService} from '../../../services/deals.service';
import {MixpanelService} from 'src/app/services/mixpanel.service';
import {finalize, take, tap} from 'rxjs/operators';
import {TierUpgradeMode, UnlockDealModalComponent} from '../unlock-deal-modal/unlock-deal-modal.component';
import {SearchSelectComponent} from '../../shared/search-select-form/search-select-form.component';
import {openModal} from 'src/app/utilities/helpers';
import {RefreshContext, RefreshTrigger} from '../../../services/refresh.service';
import {checkDepartureAirportLimit, LimitReachedAction} from '../../../helpers/user-departure-airport-limits.utils';
import {Router} from '@angular/router';

export interface DepartureAirportsModalData {
    airports: UserDeparture[];
    user: UserModel;
    maxAirportCount: number;
    dealRefreshTrigger?: RefreshTrigger;
}

interface AirportWithFrequencyClass extends Airport {
    dealFrequencyClass: string;
}

@Component({
    selector: 'departure-airports-modal',
    templateUrl: './departure-airports-modal.component.html',
    styleUrl: './departure-airports-modal.component.css',
})
export class DepartureAirportsModalComponent implements OnInit, OnDestroy {
    airports: UserDeparture[] = [];
    user: UserModel;
    maxAirportCount: number;
    hoveredAirportId: number | null = null;
    trackByAirportId = (_: number, airport: UserDeparture) => airport.id;
    dealRefreshTrigger?: RefreshTrigger;
    recommendedAirports: AirportWithFrequencyClass[] = [];
    loadingRecommendedAirport = false;
    showRecommendedAirportDropdown = false;
    disabledRecommendedAirportId: number | null = null;

    protected readonly SEARCH_CONTAINER_ID = 'departure-search-container';
    /** A departure at rank 0 is the user's primary (home) airport. */
    protected readonly PRIMARY_RANK = 0;

    @ViewChild('searchBox') searchBox: SearchSelectComponent;

    @Input() visible = false;
    @Output() onClose = new EventEmitter<void>();

    constructor(
        public ref: DynamicDialogRef,
        public config: DynamicDialogConfig<DepartureAirportsModalData>,
        public toast: ToastrService,
        private airportService: AirportsService,
        public viewport: ViewportService,
        private dealsService: DealsService,
        private mixpanel: MixpanelService,
        private dialogService: DialogService,
        private router: Router,
    ) {}

    ngOnInit() {
        this.airports = this.config.data.airports ?? [];
        this.user = this.config.data.user;
        this.maxAirportCount = this.config.data.maxAirportCount;
        this.dealRefreshTrigger = this.config.data?.dealRefreshTrigger;
        this.mixpanel.identify(this.user.id.toString());
        this.loadRecommendedAirports();
    }

    private loadRecommendedAirports(): void {
        this.loadingRecommendedAirport = true;
        this.disabledRecommendedAirportId = null;

        this.airportService
            .getRecommendedAirports()
            .pipe(
                take(1),
                finalize(() => {
                    this.loadingRecommendedAirport = false;
                }),
            )
            .subscribe({
                next: (airports) => {
                    this.recommendedAirports = airports.map((airport) => ({
                        ...airport,
                        dealFrequencyClass: this.getDealFrequencyClass(
                            airport.dealFrequency,
                        ),
                    }));
                },
                error: () => {
                    this.toast.error('Error fetching recommended airports');
                    this.recommendedAirports = [];
                },
            });
    }

    ngOnDestroy(): void {}

    searchAirports = (term: string) => {
        // Hide recommended dropdown when user starts searching
        if (term && term.trim().length > 0) {
            this.hideRecommendedDropdown();
        }
        return this.airportService.searchAirports(term);
    };

    hide() {
        this.onClose.emit();
    }

    close() {
        this.ref.close(this.airports);
    }

    addAirport = (airport: Airport) => {
        const previous = this.airports;
        //optimistic action to show the new airport immediately rather than waiting on backend
        this.airports = [
            {...airport, rank: this.airports.length},
            ...this.airports,
        ];
        this.airportService
            .setAirport(airport.id)
            .pipe(
                tap(() =>
                    this.mixpanel.track('Departure airport added', {
                        email: this.user.email,
                        airport: `${airport.name}, ${airport.code}`,
                    }),
                ),
                tap(() => {
                    this.dealRefreshTrigger?.refresh(
                        RefreshContext.DepartureModal,
                    );
                }),
            )
            .subscribe({
                next: (departures) => {
                    this.airports = departures;
                },
                error: () => {
                    this.airports = previous;
                    this.toast.error("Couldn't add airport. Please try again");
                },
            });
    };

    onSearchClick(): void {
        if (this.recommendedAirports.length > 0) {
            this.showRecommendedAirportDropdown = true;
        }
    }

    hideRecommendedDropdown(): void {
        this.showRecommendedAirportDropdown = false;
    }

    selectRecommendedAirport(airport: Airport, event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }

        const limitCheck = checkDepartureAirportLimit(
            {
                currentCount: this.airports.length,
                maxCount: this.maxAirportCount,
                isFreeUser: this.user?.isFree,
                itemLabelSingular: 'airport',
            },
            this.toast,
        );

        if (limitCheck.limitReached) {
            if (limitCheck.action !== LimitReachedAction.ReplaceOldest) {
                // Premium users: warning toast was already shown by helper
                this.hideRecommendedDropdown();
                return;
            }
            // Free users: replace their oldest airport with the newly selected one.
            // The list comes back newest-first, so the oldest is the last entry.
            const oldestAirport = this.airports[this.airports.length - 1];
            this.removeAirport(oldestAirport);
        }

        this.addAirport(airport);

        // For free users, mark as disabled instead of removing from list; for premium users, remove from list
        this.user.isFree
            ? (this.disabledRecommendedAirportId = airport.id)
            : (this.recommendedAirports = this.recommendedAirports.filter(
                  (a) => a.id !== airport.id,
              ));

        this.hideRecommendedDropdown();
    }

    isRecommendedAirportDisabled(airport: Airport): boolean {
        return this.disabledRecommendedAirportId === airport.id;
    }

    private getDealFrequencyClass(frequency: DealFrequency | null): string {
        if (!frequency) {
            return '';
        }
        return DEAL_FREQUENCY_CLASS_MAP[frequency] || '';
    }

    setPrimary(airport: UserDeparture): void {
        if (airport.rank === this.PRIMARY_RANK) {
            return;
        }
        const previous = this.airports;
        //optimistic action to switch primary label immediately rather than waiting on backend to complete
        this.airports = this.airports.map((a) => {
            if (a.id === airport.id) {
                return {...a, rank: this.PRIMARY_RANK};
            }
            return a.rank < airport.rank ? {...a, rank: a.rank + 1} : a;
        });
        this.airportService
            .updateDeparture(airport.id, {rank: this.PRIMARY_RANK})
            .pipe(
                tap(() =>
                    this.mixpanel.track('Primary departure airport set', {
                        email: this.user.email,
                        airport: `${airport.name}, ${airport.code}`,
                    }),
                ),
                tap(() => {
                    this.dealRefreshTrigger?.refresh(RefreshContext.DepartureModal);
                }),
            )
            .subscribe({
                next: (departures) => {
                    this.airports = departures;
                },
                error: () => {
                    this.airports = previous;
                    this.toast.error("Couldn't set primary airport. Please try again");
                },
            });
    }

    removeAirport(airport: UserDeparture) {
        const previous = this.airports;
        //optimistic action to drop the airport immediately rather than waiting on backend
        this.airports = this.airports
            .filter((a) => a.id !== airport.id)
            .map((a) => (a.rank > airport.rank ? {...a, rank: a.rank - 1} : a));
        this.airportService
            .removeAirport(airport.id)
            .pipe(
                tap(() =>
                    this.mixpanel.track('Departure airport removed', {
                        email: this.user.email,
                        airport: `${airport.name}, ${airport.code}`,
                    }),
                ),
                tap(() => {
                    this.dealRefreshTrigger?.refresh(
                        RefreshContext.DepartureModal,
                    );
                }),
            )
            .subscribe({
                next: (departures) => {
                    this.airports = departures;
                },
                error: () => {
                    this.airports = previous;
                    this.toast.error(
                        "Couldn't remove airport. Please try again",
                    );
                },
            });
    }

    handleAddAirportClick(): void {
        if (this.user?.isFree && this.airports.length >= this.maxAirportCount) {
            this.openUpgradeModal();
        } else {
            this.searchBox.focusInput();
        }
    }

    openUpgradeModal(): void {
        openModal(this.dialogService, UnlockDealModalComponent, {
            data: {
                isVisible: true,
                mode: TierUpgradeMode.moreAirports,
                parentRef: this.ref,
            },
            styleClass: 'dashboard-modal small-modal',
        });
    }

    goToUpgrade() {
        this.ref.close();
        this.router.navigate([this.user.upgradeLink], {
            queryParams: {
                upgrade_source: 'airports_modal',
                upgrade_campaign: '1_14_day_trial',
                upgrade_cohort: '2026',
            },
        });
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const clickedInside =
            target.closest(`#${this.SEARCH_CONTAINER_ID}`) ||
            target.closest('.dropdown-list');

        if (!clickedInside && this.showRecommendedAirportDropdown) {
            this.hideRecommendedDropdown();
        }
    }
}
